import React, { FC, useMemo } from "react";
import { Column, DataTable } from "@/components/ui/data-table";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ClipboardList, FileText } from "lucide-react";
import { SettlementBooking } from "../types/settlementBooking";
import { ActionsCell } from "./table-cells/ActionsCell";
import { MainGuestCell } from "./table-cells/MainGuestCell";
import { RoomCell } from "./table-cells/RoomCell";
import { DateCell } from "./table-cells/DateCell";
import { IndicatorCell } from "./table-cells/IndicatorCell";
import { RoomServiceCell } from "./table-cells/RoomServiceCell";
import { GuestsCell } from "./table-cells/GuestsCell";
import { InteractiveStatusCell } from "./table-cells/InteractiveStatusCell";
import { PreCheckCell } from "./table-cells/PreCheckCell";
import {
  Request as ServiceRequest,
  RequestStatus,
  RequestType,
} from "@/types/request";
import { CheckinStatus, CheckoutStatus } from "@/types/reservation";
import { useUpdateReservationStatus } from "@/hooks/useReservations";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckboxSelectionResult } from "@/hooks/useCheckboxSelection";
import { useTranslation } from "react-i18next";
import { getCurrentReservationPath } from "@/components/pages/reservations/utils/getCurrentReservationPath";
import { useCheckoutFeedbackSummaries } from "@/components/pages/feedbacks/hooks/feedbacks.hooks";
import { ReservationFeedbackBadge } from "./table-cells/ReservationFeedbackBadge";
import { formatDateTime } from "@/components/pages/reservations/utils/formatDateTime";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { NotesPopoverCell } from "@/components/common/NotesPopoverCell";
import { ReservationBalanceCell } from "@/components/pages/reservations/components/cells/ReservationBalanceCell";

export type ReservationColumnKey =
  | "select"
  | "mainGuest"
  | "guests"
  | "room"
  | "arrivalDate"
  | "balance"
  | "departureDate"
  | "roomService"
  | "requests"
  | "notesCount"
  | "reviewsCount"
  | "preCheckIn"
  | "preCheckinReadAt"
  | "preCheckOut"
  | "checkOutCompleted"
  | "checkInCompleted"
  | "documentsVerified"
  | "actions";

interface SettlementDataTableProps extends CheckboxSelectionResult {
  data: SettlementBooking[];
  visibleColumns: ReservationColumnKey[];
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick?: (reservation: SettlementBooking) => void;
  loading?: boolean;
  customColumns?: Column<SettlementBooking>[];
  showEditAction?: boolean;
}

const checkboxClass =
  "size-4 border-neutral-200 data-[state=checked]:bg-slate-500 data-[state=checked]:border-slate-500";

const getRoomServiceRequests = (
  requests: SettlementBooking["serviceRequests"] = [],
) => requests.filter((request) => request.type === RequestType.ROOM);

const getLatestTask = (request: Pick<ServiceRequest, "tasks">) =>
  [...(request.tasks ?? [])].sort(
    (leftTask, rightTask) =>
      new Date(rightTask.updatedAt).getTime() -
      new Date(leftTask.updatedAt).getTime(),
  )[0];

const getLatestRequestStatus = (
  request: Pick<ServiceRequest, "status" | "tasks">,
): RequestStatus | null => {
  const latestTask = getLatestTask(request);
  return latestTask?.status ?? request.status ?? null;
};

const isCompletedRequestStatus = (status: RequestStatus | null): boolean =>
  status === RequestStatus.DONE || status === RequestStatus.COMPLETED;

const isCanceledRequestStatus = (status: RequestStatus | null): boolean =>
  status === RequestStatus.CANCELED;

const getRequestActivityTime = (
  request: Pick<
    ServiceRequest,
    "createdAt" | "dueDateTime" | "tasks" | "updatedAt"
  >,
): number => {
  const latestTask = getLatestTask(request);
  const date =
    latestTask?.updatedAt ??
    request.updatedAt ??
    request.dueDateTime ??
    request.createdAt;

  return date ? new Date(date).getTime() : 0;
};

const getLatestRoomServiceRequest = (
  requests: SettlementBooking["serviceRequests"] = [],
): ServiceRequest | null => {
  const roomRequests = getRoomServiceRequests(requests);

  if (!roomRequests.length) {
    return null;
  }

  return [...roomRequests].sort(
    (leftRequest, rightRequest) =>
      getRequestActivityTime(rightRequest) -
      getRequestActivityTime(leftRequest),
  )[0];
};

const resolveActiveRoomServiceStatus = (
  statuses: RequestStatus[],
): RequestStatus | null => {
  if (statuses.includes(RequestStatus.IN_PROGRESS)) {
    return RequestStatus.IN_PROGRESS;
  }

  if (statuses.includes(RequestStatus.ON_HOLD)) {
    return RequestStatus.ON_HOLD;
  }

  return statuses[0] ?? null;
};

export const getRoomServiceStatus = (
  booking: SettlementBooking,
): RequestStatus | null => {
  if (booking.roomServiceStatus) {
    return booking.roomServiceStatus as RequestStatus;
  }

  const roomRequests = getRoomServiceRequests(booking.serviceRequests);
  const activeStatuses = roomRequests
    .map(getLatestRequestStatus)
    .filter((status): status is RequestStatus => {
      if (!status) {
        return false;
      }

      return (
        !isCompletedRequestStatus(status) && !isCanceledRequestStatus(status)
      );
    });

  const activeStatus = resolveActiveRoomServiceStatus(activeStatuses);
  if (activeStatus) {
    return activeStatus;
  }

  const latestRequest = getLatestRoomServiceRequest(booking.serviceRequests);
  return latestRequest ? getLatestRequestStatus(latestRequest) : null;
};

const renderRoomServiceCell = (booking: SettlementBooking) => {
  const roomServiceStatus = getRoomServiceStatus(booking);
  const hasRoomServiceRequests =
    getRoomServiceRequests(booking.serviceRequests).length > 0;

  if (!roomServiceStatus) {
    return null;
  }

  const cell = <RoomServiceCell status={roomServiceStatus} />;

  if (!hasRoomServiceRequests) {
    return cell;
  }

  return (
    <Link
      to={`/requests?type=${RequestType.ROOM}&booking=${booking.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      {cell}
    </Link>
  );
};

export const SettlementDataTable: FC<SettlementDataTableProps> = ({
  data,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
  loading = false,
  customColumns = [],
  isSelected,
  selectAll,
  toggleSelectAll,
  toggleSelectItem,
  showEditAction = true,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { capabilities, hasFeedbacksAccess } = useAccessCapabilities();

  const updateReservationMutation = useUpdateReservationStatus();
  const hasReviewsColumn =
    hasFeedbacksAccess && visibleColumns.includes("reviewsCount");

  const detailsBasePath = useMemo(() => {
    return getCurrentReservationPath(location.pathname);
  }, [location.pathname]);

  const feedbackBookingIds = useMemo(() => {
    if (!hasReviewsColumn) {
      return [];
    }

    return data.map((row) => row.id);
  }, [data, hasReviewsColumn]);

  const feedbackRoomIds = useMemo(() => {
    if (!hasReviewsColumn) {
      return [];
    }

    return data
      .map((row) => row.room?.id)
      .filter((roomId): roomId is string => Boolean(roomId));
  }, [data, hasReviewsColumn]);

  const { data: checkoutFeedbackSummaries } = useCheckoutFeedbackSummaries(
    feedbackBookingIds,
    feedbackRoomIds,
    hasFeedbacksAccess,
  );

  const resolvedVisibleColumns = useMemo(
    () =>
      visibleColumns.filter((column) => {
        const shouldHideReviews =
          !hasFeedbacksAccess && column === "reviewsCount";
        const shouldHideRequestColumns =
          !capabilities.canUseRequestsSystem &&
          (column === "roomService" || column === "requests");

        return !shouldHideReviews && !shouldHideRequestColumns;
      }),
    [capabilities.canUseRequestsSystem, hasFeedbacksAccess, visibleColumns],
  );

  const handleCheckInStatusChange = (
    reservationId: string,
    newStatus: CheckinStatus,
  ) => {
    if (newStatus) {
      updateReservationMutation.mutateAsync({
        id: reservationId,
        checkinStatus: newStatus,
      });
    }
  };

  const handleCheckOutStatusChange = (
    reservationId: string,
    newStatus: CheckoutStatus,
  ) => {
    if (newStatus) {
      updateReservationMutation.mutateAsync({
        id: reservationId,
        checkoutStatus: newStatus,
      });
    }
  };

  const allColumns: Record<ReservationColumnKey, Column<SettlementBooking>> = {
    select: {
      key: "select",
      header: (
        <Checkbox
          className={checkboxClass}
          checked={selectAll}
          onCheckedChange={toggleSelectAll}
          aria-label="Select all"
        />
      ),
      className: "text-center w-10",
      render: (_, row) => (
        <Checkbox
          className={checkboxClass}
          checked={isSelected(row.id)}
          onCheckedChange={() => {
            const syntheticEvent = {
              stopPropagation: () => {},
            } as React.MouseEvent;
            toggleSelectItem(row.id, syntheticEvent);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.guest?.profile.firstName} ${row.guest?.profile.lastName}`}
        />
      ),
    },
    mainGuest: {
      key: "guest",
      header: t("reservation.table.guestName"),
      sortable: true,
      render: (_, row) => (
        <MainGuestCell
          id={row.guest?.id}
          bookingId={row.id}
          firstName={row.guest?.profile.firstName}
          lastName={row.guest?.profile.lastName}
          nationality={row.guest?.profile.language}
          className={"justify-start"}
          flagClassName={"!h-4 !w-4"}
          fullNameClassName={"text-[10px]"}
        />
      ),
    },
    guests: {
      key: "guests",
      header: t("reservation.table.guests"),
      className: "text-center",
      sortable: true,
      render: (_, row) => {
        return (
          <GuestsCell adultsCount={row.adults} childrenCount={row.children} />
        );
      },
    },
    room: {
      key: "room",
      header: t("reservation.table.rooms"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <RoomCell
          id={row.room?.id}
          roomNumber={row.room?.number}
          roomType={row.room?.category?.name}
          className={"items-center gap-1 text-[10px]"}
          roomNumberClassName={
            "w-fit px-1 py-0.5 bg-light-gray rounded-lg text-white"
          }
        />
      ),
    },
    arrivalDate: {
      key: "arrivalDate",
      header: t("reservation.table.arrival"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <DateCell date={row.arrivalDate} timeClassName="font-medium" />
      ),
    },
    departureDate: {
      key: "departureDate",
      header: t("reservation.table.departure"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <DateCell date={row.departureDate} timeClassName="font-medium" />
      ),
    },
    roomService: {
      key: "roomService",
      header: t("reservation.table.roomService"),
      className: "text-center",
      sortable: true,
      render: (_, row) => renderRoomServiceCell(row),
    },
    requests: {
      key: "requests",
      header: t("reservation.table.requests"),
      className: "text-center",
      sortable: true,
      render: (_, row) => {
        const serviceRequests = row?.serviceRequests ?? [];
        const hasRequests = serviceRequests.length > 0;

        if (!hasRequests) return null;

        const incompleteCount = serviceRequests.filter((r) =>
          r.tasks?.some(
            (t) =>
              t.status !== RequestStatus.DONE &&
              t.status !== RequestStatus.COMPLETED,
          ),
        ).length;
        const visibleRequests =
          row?.serviceRequests?.filter(
            (request) => request.type !== RequestType.ROOM,
          ) || [];

        if (!visibleRequests.length) {
          return null;
        }

        return (
          <Link
            to={`/requests?booking=${row.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <IndicatorCell
              icon={<ClipboardList className={"text-indigo size-4"} />}
              count={incompleteCount}
              showBadge={incompleteCount > 0}
              isVisible={hasRequests}
            />
          </Link>
        );
      },
    },
    notesCount: {
      key: "notes",
      header: t("reservation.table.notes"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <NotesPopoverCell
          notes={row.notes}
          count={row.notesCount}
          title={t("reservation.notesSection.reservationNotes")}
          emptyMessage={t("reservation.notesSection.empty")}
          ariaLabel={t("reservation.notesSection.reservationNotesAria", {
            count: row.notesCount,
          })}
          to={`${detailsBasePath}/${row.id}?tab=other`}
        />
      ),
    },
    reviewsCount: {
      key: "reviewsCount",
      header: t("reservation.table.feedbacks"),
      className: "text-center",
      sortable: false,
      render: (_, row) => {
        const feedbackSummary = checkoutFeedbackSummaries?.[row.id];
        const feedbackCount = feedbackSummary?.count ?? 0;
        const feedbackRating = feedbackSummary?.lowestRating ?? null;

        return (
          <ReservationFeedbackBadge
            count={feedbackCount}
            rating={feedbackRating}
            to={
              feedbackCount > 0
                ? `${detailsBasePath}/${row.id}?tab=other`
                : undefined
            }
          />
        );
      },
    },
    balance: {
      key: "balance",
      header: t("reservation.table.balance"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <ReservationBalanceCell
          reservationId={row.id}
          embeddedTransactions={row.transactions ?? []}
        />
      ),
    },
    preCheckIn: {
      key: "preCheckIn",
      header: t("reservation.table.preCheckIn"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <PreCheckCell
          status={row.preCheckinStatus}
          readAt={row.preCheckinReadAt}
          enablePreview
          notDoneLabelKey="reservation.precheckin.preview.clicked"
          className="mx-auto min-w-fit border-[0.83px] px-2 py-0.75 text-xs"
        />
      ),
    },
    preCheckinReadAt: {
      key: "preCheckinReadAt",
      header: t("reservation.table.preCheckinReadAt"),
      className: "text-center",
      sortable: true,
      render: (_, row) =>
        row.preCheckinReadAt ? (
          <div className="text-foreground text-[10px] font-medium">
            {formatDateTime(row.preCheckinReadAt, "dd.MM.yyyy - hh:mm a")}
          </div>
        ) : (
          <div className="text-muted-foreground text-[10px]">-</div>
        ),
    },
    preCheckOut: {
      key: "preCheckOut",
      header: t("reservation.table.preCheckOut"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <PreCheckCell
          status={row.preCheckoutStatus}
          className="mx-auto min-w-fit border-[0.83px] px-2 py-0.75 text-xs"
        />
      ),
    },
    checkOutCompleted: {
      key: "checkOutCompleted",
      header: t("reservation.table.checkOut"),
      className: "text-center",
      sortable: false,
      render: (_, row) => (
        <InteractiveStatusCell
          statuses={[
            {
              status: CheckoutStatus.DONE,
              color: "border-accent-green text-accent-green",
              text: "Confirm",
            },
            {
              status: CheckoutStatus.NOT_DONE,
              color: "border-gray-5 text-gray-5",
              text: "Check-out",
            },
          ]}
          currentStatus={row.checkoutStatus}
          onStatusChange={(newStatus) =>
            handleCheckOutStatusChange(row.id, newStatus as CheckoutStatus)
          }
          className="bg-slate min-w-fit rounded-lg border-0 px-3 py-1"
          labelClassName="text-white text-xs"
          dropdownMenuItemClassName="text-xs"
        />
      ),
    },
    checkInCompleted: {
      key: "checkInCompleted",
      header: t("reservation.table.checkIn"),
      className: "text-center",
      sortable: false,
      render: (_, row) => (
        <InteractiveStatusCell
          statuses={[
            {
              status: CheckinStatus.DONE,
              color: "border-accent-green text-accent-green",
              text: "Confirm",
            },
            {
              status: CheckinStatus.NOT_DONE,
              color: "border-gray-5 text-gray-5",
              text: "Check-in",
            },
          ]}
          currentStatus={row.checkinStatus}
          onStatusChange={(newStatus) =>
            handleCheckInStatusChange(row.id, newStatus as CheckinStatus)
          }
          className="bg-slate min-w-fit rounded-lg border-0 px-3 py-1"
          labelClassName="text-white text-xs"
          dropdownMenuItemClassName="text-xs"
        />
      ),
    },
    documentsVerified: {
      key: "documentsVerified",
      header: t("reservation.table.document"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <IndicatorCell
          icon={<FileText className={"text-indigo size-4"} />}
          isDimmed={row?.guest?.documents?.every((d) => d.isApproved) ?? false}
        />
      ),
    },
    actions: {
      key: "actions",
      header: t("reservation.table.actions"),
      className: "text-center",
      render: (_, row) => (
        <ActionsCell
          id={row.id}
          showEditAction={showEditAction}
          detailsBasePath={detailsBasePath}
        />
      ),
    },
  };

  const columns = [
    ...resolvedVisibleColumns.map((columnKey) => allColumns[columnKey]),
    ...customColumns,
  ];

  const navigate = useNavigate();

  const handleRowClicked = (
    reservation: SettlementBooking,
    _index: number,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    if ((event.target as HTMLElement).closest("a, button")) {
      return;
    }
    if (onRowClick) {
      onRowClick(reservation);
    }
    navigate(`${detailsBasePath}/${reservation.id}`);
  };

  return (
    <DataTable
      data={data}
      columns={columns}
      loading={loading}
      getRowKey={(row) => row.id}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      sorting={{
        field: sortField || "",
        direction: sortDirection || "asc",
        onSort,
      }}
      emptyMessage="No reservations found"
      onRowClick={handleRowClicked}
      iconClassName="text-neutral-500"
      columnHeaderClassName="text-neutral-500 text-xs font-medium"
    />
  );
};
